import type { PlayerSlot } from '@chess/core'

/**
 * Everything here is a convenience cache, never a source of truth. A player's
 * seat lives in the link they hold; losing local storage costs you the room
 * *list*, not the ability to rejoin a room.
 */
const NAME_KEY = 'chess:name'
const ROOMS_KEY = 'chess:rooms'
const MAX_ROOMS = 12

const read = <T>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key)
    return raw === null ? fallback : (JSON.parse(raw) as T)
  } catch {
    return fallback
  }
}

const write = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* Private browsing, or the quota is full. Not worth surfacing. */
  }
}

export const loadName = (): string => read<string>(NAME_KEY, '')
export const saveName = (name: string): void => write(NAME_KEY, name)

export interface RecentRoom {
  readonly roomId: string
  readonly key: string
  readonly slot: PlayerSlot
  readonly opponentName: string
  readonly timeControl: string
  readonly wins: number
  readonly losses: number
  readonly draws: number
  readonly yourTurn: boolean
  readonly finished: boolean
  readonly updatedAt: number
}

export const loadRooms = (): readonly RecentRoom[] =>
  read<RecentRoom[]>(ROOMS_KEY, []).filter((room) => typeof room?.roomId === 'string')

export const rememberRoom = (room: RecentRoom): void => {
  const others = loadRooms().filter((existing) => existing.roomId !== room.roomId)
  write(ROOMS_KEY, [room, ...others].slice(0, MAX_ROOMS))
}

export const forgetRoom = (roomId: string): void => {
  write(
    ROOMS_KEY,
    loadRooms().filter((room) => room.roomId !== roomId),
  )
}
